import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { BRAND } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    property_type: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Udfyld venligst navn, e-mail og besked.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Tak for din henvendelse - vi vender tilbage hurtigst muligt.");
      setForm({ name: "", email: "", phone: "", property_type: "", message: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Kunne ikke sende henvendelsen. Prøv igen om et øjeblik.";
      toast.error(typeof detail === "string" ? detail : "Noget gik galt.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-[#111111] text-white py-16 sm:py-28 relative overflow-hidden"
    >
      {/* Ambient glow */}
      <div className="absolute -top-32 right-0 w-[34rem] h-[34rem] glow-gold pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="lg:col-span-5"
          >
            <p className="overline mb-4 sm:mb-5 text-[10px] sm:text-xs" style={{ color: "#D4AF37" }}>
              <span className="inline-block w-8 sm:w-10 h-px bg-[#D4AF37] align-middle mr-3 sm:mr-4" />
              Kontakt
            </p>
            <h2
              className="font-serif text-[2.25rem] sm:text-5xl lg:text-[3.5rem] text-white font-light tracking-tight leading-[1.05]"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Klar til at slippe for den{" "}
              <span className="italic text-[#D4AF37]">daglige drift?</span>
            </h2>

            <p className="mt-8 text-white/70 font-light leading-relaxed max-w-md">
              Skriv til os og fortæl kort om din bolig. Vi vender tilbage inden
              for 24 timer med en uforpligtende vurdering.
            </p>

            <div className="mt-12 space-y-8">
              <div>
                <div className="overline mb-2" style={{ color: "#D4AF37" }}>
                  E-mail
                </div>
                <a
                  href={`mailto:${BRAND.email}`}
                  data-testid="contact-email-link"
                  className="font-serif text-2xl sm:text-3xl text-white link-underline"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  {BRAND.email}
                </a>
              </div>

              <div className="h-px w-full bg-white/10" />

              <div>
                <div className="overline mb-2" style={{ color: "#D4AF37" }}>
                  Telefon
                </div>
                <a
                  href={`tel:${BRAND.phoneRaw}`}
                  data-testid="contact-phone-link"
                  className="font-serif text-2xl sm:text-3xl text-white link-underline"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  {BRAND.phone}
                </a>
              </div>

              <div className="h-px w-full bg-white/10" />

              <div>
                <div className="overline mb-2" style={{ color: "#D4AF37" }}>
                  Book direkte
                </div>
                <a
                  href={BRAND.calendlyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-calendly-link"
                  className="font-serif text-2xl sm:text-3xl text-white link-underline inline-flex items-center gap-3"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  30 min uforpligtende samtale
                  <ArrowRight size={20} />
                </a>
              </div>
            </div>
          </motion.div>

          <motion.form
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.15, ease: "easeOut" }}
            className="lg:col-span-7 relative"
            data-testid="contact-form"
          >
            <div className="relative bg-white/[0.04] border border-white/10 p-6 sm:p-10 lg:p-12">
              {/* Gold corner accents */}
              <div className="absolute top-0 left-0 w-10 h-10 border-t border-l border-[#D4AF37]/60 pointer-events-none" />
              <div className="absolute top-0 right-0 w-10 h-10 border-t border-r border-[#D4AF37]/60 pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-10 h-10 border-b border-l border-[#D4AF37]/60 pointer-events-none" />
              <div className="absolute bottom-0 right-0 w-10 h-10 border-b border-r border-[#D4AF37]/60 pointer-events-none" />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
              <div>
                <label className="overline block mb-3" style={{ color: "#D4AF37" }}>
                  Navn *
                </label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={update("name")}
                  className="field-line field-line-dark"
                  placeholder="Dit fulde navn"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <label className="overline block mb-3" style={{ color: "#D4AF37" }}>
                  E-mail *
                </label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  className="field-line field-line-dark"
                  placeholder="dig@email.dk"
                  data-testid="contact-email-input"
                />
              </div>
              <div>
                <label className="overline block mb-3" style={{ color: "#D4AF37" }}>
                  Telefon
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  className="field-line field-line-dark"
                  placeholder="+45 ..."
                  data-testid="contact-phone-input"
                />
              </div>
              <div>
                <label className="overline block mb-3" style={{ color: "#D4AF37" }}>
                  Type bolig
                </label>
                <input
                  type="text"
                  value={form.property_type}
                  onChange={update("property_type")}
                  className="field-line field-line-dark"
                  placeholder="Lejlighed, sommerhus, villa ..."
                  data-testid="contact-property-input"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="overline block mb-3" style={{ color: "#D4AF37" }}>
                  Besked *
                </label>
                <textarea
                  required
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  className="field-line field-line-dark resize-none"
                  placeholder="Fortæl kort om din bolig og dine ønsker ..."
                  data-testid="contact-message-input"
                />
              </div>
            </div>

              <div className="mt-10 sm:mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-6 pt-8 border-t border-white/10">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-form-submit-btn"
                  className="inline-flex items-center gap-3 bg-[#D4AF37] text-[#111111] px-8 py-4 text-xs uppercase tracking-[0.18em] hover:bg-white transition-colors duration-500 disabled:opacity-60"
                >
                  <span>{submitting ? "Sender ..." : "Send henvendelse"}</span>
                  <ArrowRight size={16} />
                </button>
                <p className="text-xs text-white/55 tracking-wide max-w-sm">
                  Ved at sende henvendelsen accepterer du, at vi kontakter dig pr.
                  e-mail eller telefon vedrørende din forespørgsel.
                </p>
              </div>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
